import { useEffect, useState, useRef } from "react";
import axios from "axios";
import "@/App.css";
import {
  ArrowRight,
  Truck,
  Phone,
  Mail,
  MapPin,
  FileText,
  Headphones,
  Handshake,
  Search,
  Calculator,
  ShieldCheck,
  Clock,
  TrendingUp,
  Package,
  CheckCircle2,
  MenuIcon,
  X as CloseIcon,
  ChevronRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";
const HERO_BG =
  "https://images.pexels.com/photos/11053643/pexels-photo-11053643.jpeg";
const SERVICES_BG =
  "https://images.pexels.com/photos/2804929/pexels-photo-2804929.jpeg";
const CONTACT_BG =
  "https://images.unsplash.com/photo-1699444115784-8025ce1da6b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwbmlnaHR8ZW58MHx8fHwxNzc2ODk1ODc3fDA&ixlib=rb-4.1.0&q=85";

/* ---------- NAVBAR ---------- */
const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  const links = [
    { href: "#services", label: "Services" },
    { href: "#why-us", label: "Why Us" },
    { href: "#pricing", label: "Pricing" },
    { href: "#about", label: "About" },
    { href: "#contact", label: "Contact" },
  ];
  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0A0A0A]/95 backdrop-blur-md border-b border-[#262626]"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 md:px-8 max-w-7xl flex items-center justify-between h-20">
        <a href="#top" className="flex items-center gap-3" data-testid="nav-logo-link">
          <img src={LOGO_URL} alt="Synnify" className="h-11 w-11 object-contain" />
          <span className="font-display text-xl font-black tracking-tight uppercase text-white">
            Synnify
          </span>
        </a>
        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="nav-link"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href="#onboarding"
            className="hidden md:inline-flex btn-primary text-sm"
            data-testid="nav-onboard-cta"
          >
            Onboard Now <ArrowRight size={16} strokeWidth={3} />
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden border-2 border-[#262626] p-2 text-white hover:border-[#FF5A1F] transition"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <CloseIcon size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#262626] bg-[#0A0A0A]" data-testid="mobile-menu">
          <div className="container mx-auto px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="nav-link text-lg"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#onboarding"
              onClick={() => setOpen(false)}
              className="btn-primary text-sm justify-center mt-2"
              data-testid="mobile-nav-onboard"
            >
              Onboard Now <ArrowRight size={16} strokeWidth={3} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

/* ---------- HERO ---------- */
const Hero = () => (
  <section
    id="top"
    className="relative min-h-screen flex items-center overflow-hidden noise"
    data-testid="hero-section"
  >
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_BG})` }}
    />
    <div className="absolute inset-0 hero-overlay" />
    <div className="absolute inset-0 grid-lines opacity-60" />

    <div className="relative z-10 container mx-auto px-6 md:px-8 max-w-7xl pt-28 pb-20">
      <div className="max-w-4xl">
        <div className="pill reveal mb-8" data-testid="hero-status-pill">
          <span className="pulse" />
          <span>Dispatch Ops • Active 24/7</span>
        </div>
        <h1
          className="reveal reveal-delay-1 font-display text-5xl sm:text-6xl md:text-7xl lg:text-[86px] font-black uppercase leading-[0.92] tracking-tighter text-white"
          data-testid="hero-headline"
        >
          We drive your <span className="text-[#FF5A1F]">profit.</span>
          <br />
          You drive the <span className="relative inline-block">
            truck.
            <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 300 14" preserveAspectRatio="none">
              <path d="M2 10 Q 75 2, 150 8 T 298 6" stroke="#FF5A1F" strokeWidth="4" fill="none" strokeLinecap="square" />
            </svg>
          </span>
        </h1>
        <p className="reveal reveal-delay-2 mt-8 text-lg md:text-xl text-[#A3A3A3] max-w-2xl leading-relaxed" data-testid="hero-subheadline">
          Synnify LLC is a truck dispatch &amp; logistics support company built for owner-operators
          and small fleets. We source freight, negotiate rates, and handle the paperwork — so every
          mile is a paid mile.
        </p>
        <div className="reveal reveal-delay-3 mt-10 flex flex-wrap gap-4">
          <a href="#onboarding" className="btn-primary" data-testid="hero-cta-onboard">
            Start Hauling <ArrowRight size={18} strokeWidth={3} />
          </a>
          <a href="#pricing" className="btn-secondary" data-testid="hero-cta-pricing">
            View Plans
          </a>
        </div>

        <div className="reveal reveal-delay-4 mt-16 flex flex-wrap gap-8 text-[#A3A3A3] font-mono-plex text-xs uppercase tracking-widest">
          <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#FF5A1F]" strokeWidth={2.5} /> No Long-Term Contracts</div>
          <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#FF5A1F]" strokeWidth={2.5} /> 48 Contiguous States</div>
          <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#FF5A1F]" strokeWidth={2.5} /> Transparent Pricing</div>
        </div>
      </div>
    </div>

    {/* Bottom coordinate strip */}
    <div className="absolute bottom-6 left-0 right-0 z-10">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl flex justify-between items-center text-[#737373] font-mono-plex text-xs uppercase tracking-widest">
        <span>29.7°N / 95.3°W — Houston, TX</span>
        <span className="hidden md:inline">Scroll ↓</span>
      </div>
    </div>
  </section>
);

/* ---------- MARQUEE ---------- */
const Marquee = () => {
  const items = [
    "Load Sourcing",
    "Rate Negotiation",
    "Broker Comms",
    "Paperwork Handled",
    "24/7 Support",
    "Pickup &amp; Delivery",
    "Higher RPM",
    "Owner-Operators",
    "Small Fleets",
  ];
  return (
    <div className="relative border-y border-[#262626] bg-[#0d0d0d] py-6 overflow-hidden" data-testid="marquee-section">
      <div className="marquee">
        {[...items, ...items].map((t, i) => (
          <span key={i} className="ticker-item">
            {t} <span className="dot">◆</span>
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------- STATS ---------- */
const useCounter = (end, duration = 2000, start = 0) => {
  const [val, setVal] = useState(start);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let started = false;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) {
          started = true;
          const t0 = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - t0) / duration);
            setVal(Math.floor(start + (end - start) * (1 - Math.pow(1 - p, 3))));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration, start]);
  return [val, ref];
};

const StatItem = ({ value, suffix, label, index }) => {
  const [v, r] = useCounter(value);
  return (
    <div ref={r} className="relative" data-testid={`stat-item-${index}`}>
      <div className="font-display text-6xl md:text-7xl font-black text-white leading-none">
        {v.toLocaleString()}
        <span className="text-[#FF5A1F]">{suffix}</span>
      </div>
      <div className="mt-4 text-[#A3A3A3] uppercase tracking-widest text-xs font-mono-plex">
        {label}
      </div>
    </div>
  );
};

const Stats = () => (
  <section className="relative border-b border-[#262626] bg-[#0A0A0A]" data-testid="stats-section">
    <div className="container mx-auto px-6 md:px-8 max-w-7xl py-20">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6">
        <StatItem index={0} value={4200} suffix="+" label="Loads Secured" />
        <StatItem index={1} value={180} suffix="+" label="Active Carriers" />
        <StatItem index={2} value={27} suffix="%" label="Avg. RPM Increase" />
        <StatItem index={3} value={24} suffix="/7" label="Dispatch Coverage" />
      </div>
    </div>
  </section>
);

/* ---------- DISCLAIMER BAND ---------- */
const Disclaimer = () => (
  <section className="relative py-10 bg-[#141414] border-y border-[#262626]" data-testid="disclaimer-band">
    <div className="container mx-auto px-6 md:px-8 max-w-7xl flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <ShieldCheck className="text-[#FF5A1F]" size={28} strokeWidth={2.5} />
        <p className="font-display text-lg md:text-xl text-white tracking-tight">
          <span className="text-[#FF5A1F] font-black uppercase">Not</span> a broker.{" "}
          <span className="text-[#FF5A1F] font-black uppercase">Not</span> a carrier.{" "}
          <span className="text-white font-black uppercase">Pure dispatch support.</span>
        </p>
      </div>
      <span className="stamp">Synnify LLC • EST. Houston, TX</span>
    </div>
  </section>
);

/* ---------- SERVICES BENTO ---------- */
const serviceDefs = [
  {
    icon: Search,
    title: "Load Sourcing",
    desc: "We scour top load boards (DAT, Truckstop, direct shipper lanes) and cherry-pick freight that matches your equipment, home base, and profit goals.",
    big: true,
  },
  { icon: Calculator, title: "Rate Negotiation", desc: "We negotiate hard with brokers — using real market data — so you never leave money on the table." },
  { icon: Package, title: "Pickup & Delivery Coordination", desc: "From appointment setting to check-calls, we choreograph every leg of the load." },
  { icon: FileText, title: "Paperwork & Admin", desc: "Rate cons, BOLs, carrier packets, COI requests — handled so your cab stays paperless." },
  { icon: Handshake, title: "Broker Communication", desc: "We become your voice — professional, persistent, and always on-brand." },
  { icon: Headphones, title: "24/7 Dispatch Support", desc: "Breakdown, reroute, weather delay — our desk is always live when you need it." },
];

const ServiceCard = ({ icon: Icon, title, desc, big, index }) => {
  const onMove = (e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${((e.clientX - rect.left) / rect.width) * 100}%`);
    el.style.setProperty("--my", `${((e.clientY - rect.top) / rect.height) * 100}%`);
  };
  return (
    <div
      onMouseMove={onMove}
      className={`bento-card ${big ? "md:col-span-6 md:row-span-2" : "md:col-span-3"}`}
      data-testid={`service-card-${index}`}
    >
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-start justify-between">
          <div className="w-14 h-14 border border-[#262626] flex items-center justify-center bg-[#0A0A0A] group-hover:border-[#FF5A1F] transition">
            <Icon className="text-[#FF5A1F]" size={26} strokeWidth={2.5} />
          </div>
          <span className="font-mono-plex text-xs text-[#737373] uppercase tracking-widest">
            0{index + 1}
          </span>
        </div>
        <h3 className={`mt-8 font-display font-bold tracking-tight text-white ${big ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"}`}>
          {title}
        </h3>
        <p className={`mt-4 text-[#A3A3A3] leading-relaxed ${big ? "text-base md:text-lg max-w-lg" : "text-sm"}`}>
          {desc}
        </p>
        {big && (
          <div className="mt-auto pt-8 flex items-center gap-2 text-[#FF5A1F] font-display font-bold uppercase tracking-widest text-sm">
            Our core service <ChevronRight size={18} strokeWidth={3} />
          </div>
        )}
      </div>
    </div>
  );
};

const Services = () => (
  <section id="services" className="relative py-24 md:py-32 overflow-hidden" data-testid="services-section">
    <div
      className="absolute inset-0 opacity-[0.08] bg-cover bg-center"
      style={{ backgroundImage: `url(${SERVICES_BG})` }}
    />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]" />
    <div className="relative container mx-auto px-6 md:px-8 max-w-7xl">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 items-end">
        <div className="md:col-span-7">
          <span className="stamp mb-4 block">// 01 — Services</span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95]">
            Full back-office for <span className="text-[#FF5A1F]">hard-working</span> carriers.
          </h2>
        </div>
        <p className="md:col-span-5 text-lg text-[#A3A3A3] leading-relaxed">
          Synnify plugs into your operation like a co-driver. We handle everything off the truck so
          you keep wheels turning and revenue rolling — no more nights spent on load boards.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {serviceDefs.map((s, i) => (
          <ServiceCard key={i} index={i} {...s} />
        ))}
      </div>
    </div>
  </section>
);

/* ---------- WHY US ---------- */
const whyItems = [
  { n: "01", t: "Dispatchers with trucking DNA", d: "Our team knows the difference between a dry van and a reefer, HOS and detention, and how to read a broker bluff." },
  { n: "02", t: "Higher paying loads", d: "We cherry-pick high-RPM freight and negotiate assertively — average clients see 20-30% revenue lift." },
  { n: "03", t: "100% transparent pricing", d: "Flat % or flat weekly. No hidden fees, no factoring lock-ins, no surprise invoices." },
  { n: "04", t: "You stay in control", d: "You approve every load before we book it. Your truck, your rules." },
  { n: "05", t: "Paperwork? Gone.", d: "We handle packets, rate confirmations, COI requests, and broker set-ups end-to-end." },
  { n: "06", t: "After-hours coverage", d: "Breakdowns and reroutes don't respect office hours. Neither do we." },
];

const WhyUs = () => (
  <section id="why-us" className="relative py-24 md:py-32 bg-[#0d0d0d] border-y border-[#262626]" data-testid="why-us-section">
    <div className="container mx-auto px-6 md:px-8 max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
      <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
        <span className="stamp mb-4 block">// 02 — Why Synnify</span>
        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95]">
          Built <span className="text-[#FF5A1F]">by</span> carriers.
          <br />
          Run <span className="text-[#FF5A1F]">for</span> carriers.
        </h2>
        <p className="mt-6 text-[#A3A3A3] leading-relaxed text-lg">
          We don't treat trucks like spreadsheets. We treat them like the business they are — and
          we work every angle to keep yours profitable.
        </p>
      </div>

      <div className="lg:col-span-8 space-y-4">
        {whyItems.map((it, i) => (
          <div
            key={i}
            className="border border-[#262626] p-6 md:p-8 bg-[#141414] hover:border-[#FF5A1F] hover:bg-[#1C1C1C] transition-all duration-300 group"
            data-testid={`why-item-${i}`}
          >
            <div className="flex gap-6 items-start">
              <span className="font-display text-5xl md:text-6xl font-black text-[#262626] group-hover:text-[#FF5A1F] transition leading-none">
                {it.n}
              </span>
              <div>
                <h3 className="font-display text-xl md:text-2xl font-bold text-white tracking-tight">
                  {it.t}
                </h3>
                <p className="mt-2 text-[#A3A3A3] leading-relaxed">{it.d}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- PRICING ---------- */
const pricingPlans = [
  {
    name: "Dispatch Percentage",
    price: "5%",
    unit: "of gross per load",
    pitch: "Best for owner-operators looking to scale revenue without fixed overhead.",
    features: [
      "Unlimited load sourcing",
      "Rate negotiation on every booking",
      "Rate con & paperwork handled",
      "Broker packet setup",
      "24/7 dispatch hotline",
      "Weekly revenue summary",
    ],
    badge: "Most Popular",
    featured: true,
    testid: "pricing-plan-percent",
  },
  {
    name: "Flat Weekly",
    price: "$350",
    unit: "per truck / week",
    pitch: "Best for fleets wanting predictable, fixed dispatch costs at scale.",
    features: [
      "Everything in Percentage plan",
      "Dedicated fleet dispatcher",
      "Multi-truck coordination",
      "Priority lane hunting",
      "Driver-friendly load matching",
      "Custom reporting",
    ],
    badge: null,
    featured: false,
    testid: "pricing-plan-flat",
  },
];

const Pricing = () => (
  <section id="pricing" className="relative py-24 md:py-32" data-testid="pricing-section">
    <div className="container mx-auto px-6 md:px-8 max-w-7xl">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <span className="stamp block mb-4">// 03 — Pricing</span>
        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95]">
          Simple, honest <span className="text-[#FF5A1F]">pricing.</span>
        </h2>
        <p className="mt-6 text-[#A3A3A3] text-lg leading-relaxed">
          Two clear paths. Zero hidden fees. Cancel anytime — no contracts, no factoring strings
          attached.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
        {pricingPlans.map((p) => (
          <div
            key={p.name}
            data-testid={p.testid}
            className={`relative p-8 md:p-10 border-2 transition-all duration-300 ${
              p.featured
                ? "border-[#FF5A1F] bg-gradient-to-b from-[#1a0f09] to-[#141414]"
                : "border-[#262626] bg-[#141414] hover:border-[#FF5A1F]"
            }`}
          >
            {p.badge && (
              <div className="absolute -top-4 left-8 bg-[#FF5A1F] text-white font-display font-bold uppercase tracking-widest text-xs px-4 py-2">
                {p.badge}
              </div>
            )}
            <div className="flex items-baseline justify-between">
              <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight text-white">
                {p.name}
              </h3>
              <Truck className={p.featured ? "text-[#FF5A1F]" : "text-[#737373]"} size={28} strokeWidth={2.5} />
            </div>
            <div className="mt-8 flex items-end gap-3">
              <span className="font-display text-6xl md:text-7xl font-black text-white leading-none">
                {p.price}
              </span>
              <span className="text-[#A3A3A3] font-mono-plex text-xs uppercase tracking-widest pb-2">
                {p.unit}
              </span>
            </div>
            <p className="mt-4 text-[#A3A3A3] leading-relaxed">{p.pitch}</p>
            <ul className="mt-8 space-y-3">
              {p.features.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-white">
                  <CheckCircle2 className="text-[#FF5A1F] mt-0.5 shrink-0" size={18} strokeWidth={2.5} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <a
              href="#onboarding"
              className={`mt-10 w-full ${p.featured ? "btn-primary" : "btn-secondary"} justify-center`}
              data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              Get Started <ArrowRight size={16} strokeWidth={3} />
            </a>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- ONBOARDING FORM ---------- */
const emptyOnboarding = {
  full_name: "",
  company_name: "",
  email: "",
  phone: "",
  mc_number: "",
  dot_number: "",
  truck_type: "",
  fleet_size: "1",
  preferred_lanes: "",
  notes: "",
};

const Onboarding = () => {
  const [form, setForm] = useState(emptyOnboarding);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.truck_type) {
      toast.error("Please fill in name, email, phone, and truck type.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/onboarding`, form);
      toast.success("Application received! We'll be in touch within 1 business day.");
      setForm(emptyOnboarding);
    } catch (err) {
      toast.error("Submission failed. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="onboarding" className="relative py-24 md:py-32 bg-[#0d0d0d] border-y border-[#262626]" data-testid="onboarding-section">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="stamp block mb-4">// 04 — Carrier Onboarding</span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95]">
            Start hauling <span className="text-[#FF5A1F]">smarter.</span>
          </h2>
          <p className="mt-6 text-[#A3A3A3] text-lg leading-relaxed">
            Fill out the onboarding form and our team will call you within one business day to walk
            through setup, paperwork, and lane preferences.
          </p>
          <div className="mt-10 space-y-5">
            {[
              ["We review your MC/DOT & insurance.", "5 min"],
              ["Sign a dispatch agreement.", "10 min"],
              ["Load packet goes to brokers.", "Same day"],
              ["First load booked.", "24–72 hours"],
            ].map(([t, time], i) => (
              <div key={i} className="flex items-start gap-4">
                <span className="font-display font-black text-[#FF5A1F] text-xl w-8">0{i + 1}</span>
                <div>
                  <p className="text-white font-semibold">{t}</p>
                  <p className="text-[#737373] text-sm font-mono-plex uppercase tracking-widest">{time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <form
          onSubmit={submit}
          className="lg:col-span-7 bg-[#141414] border border-[#262626] p-6 md:p-10 corner-brackets"
          data-testid="onboarding-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="stamp block mb-2">Full Name *</label>
              <input className="industrial-input" placeholder="John Driver" value={form.full_name} onChange={set("full_name")} data-testid="onboarding-fullname" />
            </div>
            <div>
              <label className="stamp block mb-2">Company Name</label>
              <input className="industrial-input" placeholder="Driver Trucking LLC" value={form.company_name} onChange={set("company_name")} data-testid="onboarding-company" />
            </div>
            <div>
              <label className="stamp block mb-2">Email *</label>
              <input type="email" className="industrial-input" placeholder="you@trucking.com" value={form.email} onChange={set("email")} data-testid="onboarding-email" />
            </div>
            <div>
              <label className="stamp block mb-2">Phone *</label>
              <input className="industrial-input" placeholder="(555) 555-5555" value={form.phone} onChange={set("phone")} data-testid="onboarding-phone" />
            </div>
            <div>
              <label className="stamp block mb-2">MC Number</label>
              <input className="industrial-input" placeholder="MC-123456" value={form.mc_number} onChange={set("mc_number")} data-testid="onboarding-mc" />
            </div>
            <div>
              <label className="stamp block mb-2">DOT Number</label>
              <input className="industrial-input" placeholder="1234567" value={form.dot_number} onChange={set("dot_number")} data-testid="onboarding-dot" />
            </div>
            <div>
              <label className="stamp block mb-2">Truck Type *</label>
              <Select value={form.truck_type} onValueChange={(v) => setForm((f) => ({ ...f, truck_type: v }))}>
                <SelectTrigger className="industrial-input rounded-none h-[52px]" data-testid="onboarding-trucktype-trigger">
                  <SelectValue placeholder="Select equipment" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border border-[#262626] rounded-none text-white">
                  {["Dry Van", "Reefer", "Flatbed", "Step Deck", "Power Only", "Hotshot", "Tanker", "Box Truck"].map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-[#FF5A1F] focus:text-white rounded-none">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="stamp block mb-2">Fleet Size</label>
              <Select value={form.fleet_size} onValueChange={(v) => setForm((f) => ({ ...f, fleet_size: v }))}>
                <SelectTrigger className="industrial-input rounded-none h-[52px]" data-testid="onboarding-fleetsize-trigger">
                  <SelectValue placeholder="Number of trucks" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border border-[#262626] rounded-none text-white">
                  {["1", "2-5", "6-10", "11-25", "25+"].map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-[#FF5A1F] focus:text-white rounded-none">{t} truck(s)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="stamp block mb-2">Preferred Lanes</label>
              <input className="industrial-input" placeholder="e.g. TX ↔ CA, Midwest regional" value={form.preferred_lanes} onChange={set("preferred_lanes")} data-testid="onboarding-lanes" />
            </div>
            <div className="md:col-span-2">
              <label className="stamp block mb-2">Additional Notes</label>
              <textarea className="industrial-input" placeholder="Anything we should know about your operation?" value={form.notes} onChange={set("notes")} data-testid="onboarding-notes" />
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-xs text-[#737373] font-mono-plex uppercase tracking-widest">
              * Required • We reply within 24 hours
            </p>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary disabled:opacity-60"
              data-testid="onboarding-submit"
            >
              {loading ? "Submitting..." : "Start Hauling"} <ArrowRight size={18} strokeWidth={3} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

/* ---------- ABOUT ---------- */
const About = () => (
  <section id="about" className="relative py-24 md:py-32" data-testid="about-section">
    <div className="container mx-auto px-6 md:px-8 max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5 relative">
        <div
          className="aspect-[4/5] bg-cover bg-center border border-[#262626]"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute -bottom-6 -right-6 bg-[#FF5A1F] p-6 max-w-[220px]">
          <div className="font-display text-5xl font-black text-white leading-none">EST.</div>
          <div className="font-display text-3xl font-black text-white leading-none mt-1">Houston</div>
          <div className="mt-2 font-mono-plex text-xs uppercase tracking-widest text-white/80">TX • USA</div>
        </div>
      </div>
      <div className="lg:col-span-7">
        <span className="stamp block mb-4">// About Synnify</span>
        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95]">
          A dispatch partner that actually <span className="text-[#FF5A1F]">gets it.</span>
        </h2>
        <p className="mt-6 text-[#A3A3A3] text-lg leading-relaxed">
          Synnify LLC is a logistics support company specializing in truck dispatch services for
          independent owner-operators and small fleet carriers. We source freight through load
          boards, negotiate rates with brokers, coordinate pickups and deliveries, and manage the
          administrative side — paperwork, rate confirmations, and broker communication.
        </p>
        <p className="mt-4 text-[#A3A3A3] text-lg leading-relaxed">
          <span className="text-white font-semibold">Important:</span> Synnify LLC does not operate
          trucks, and we are not a freight carrier or broker. We provide support services to help
          carriers maximize efficiency and profitability — that's our lane, and we stay in it.
        </p>

        <div className="mt-10 grid grid-cols-2 gap-6">
          <div className="border-l-2 border-[#FF5A1F] pl-5">
            <div className="font-display text-3xl font-black text-white">Mission</div>
            <p className="text-[#A3A3A3] mt-2 text-sm leading-relaxed">
              Keep every client's truck booked on the best-paying lanes — without the load-board grind.
            </p>
          </div>
          <div className="border-l-2 border-[#FF5A1F] pl-5">
            <div className="font-display text-3xl font-black text-white">Vision</div>
            <p className="text-[#A3A3A3] mt-2 text-sm leading-relaxed">
              Become the most trusted back-office for owner-operators across the 48 states.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- FAQ ---------- */
const faqs = [
  { q: "Are you a broker or a carrier?", a: "Neither. Synnify LLC is a logistics support company. We provide dispatch services — sourcing loads, negotiating with brokers, and handling paperwork on your behalf. We never take legal possession of freight or dispatch our own trucks." },
  { q: "How do you charge?", a: "Two options: a transparent percentage of each load's gross (typically 5%) or a flat weekly fee per truck ($350/week). No contracts, no factoring lock-ins, no surprise fees." },
  { q: "Do I need to sign a long-term contract?", a: "No. Our agreement is month-to-month and cancellable with written notice. We believe in earning your business every week." },
  { q: "What equipment do you dispatch?", a: "Dry Van, Reefer, Flatbed, Step Deck, Power Only, Hotshot, Box Truck, and Tanker. If your trailer rolls, we can book it." },
  { q: "How fast can I get my first load?", a: "Typical timeline is 24–72 hours from the moment we receive your signed agreement, MC authority, COI, and W-9." },
  { q: "Do you work with new authorities?", a: "Yes. We work with new MCs (under 90 days) on select lanes. Brokers may impose minimums, but we'll match you with freight that doesn't require seasoning." },
];

const FAQ = () => (
  <section className="relative py-24 md:py-32 bg-[#0d0d0d] border-y border-[#262626]" data-testid="faq-section">
    <div className="container mx-auto px-6 md:px-8 max-w-5xl">
      <div className="text-center mb-14">
        <span className="stamp block mb-4">// Frequently Asked</span>
        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95]">
          Questions? We've got <span className="text-[#FF5A1F]">answers.</span>
        </h2>
      </div>
      <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
        {faqs.map((f, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className="bg-[#141414] border border-[#262626] rounded-none data-[state=open]:border-[#FF5A1F] transition-colors px-6"
            data-testid={`faq-item-${i}`}
          >
            <AccordionTrigger className="font-display text-lg md:text-xl font-bold text-white hover:no-underline hover:text-[#FF5A1F] text-left py-6">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-[#A3A3A3] leading-relaxed text-base pb-6">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

/* ---------- CONTACT ---------- */
const emptyContact = { name: "", email: "", phone: "", subject: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(emptyContact);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll reply shortly.");
      setForm(emptyContact);
    } catch (err) {
      toast.error("Could not send. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 bg-cover bg-center opacity-[0.12]" style={{ backgroundImage: `url(${CONTACT_BG})` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]" />
      <div className="relative container mx-auto px-6 md:px-8 max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="stamp block mb-4">// 05 — Contact</span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95]">
            Let's get your truck <span className="text-[#FF5A1F]">loaded.</span>
          </h2>
          <p className="mt-6 text-[#A3A3A3] text-lg leading-relaxed">
            Call, email, or send a message — someone at the Synnify desk is always ready to talk
            lanes, rates, and freight.
          </p>

          <div className="mt-10 space-y-6">
            <a href="tel:+12018221260" className="flex items-start gap-5 group" data-testid="contact-phone">
              <div className="w-12 h-12 border border-[#262626] flex items-center justify-center group-hover:border-[#FF5A1F] group-hover:bg-[#FF5A1F]/10 transition">
                <Phone className="text-[#FF5A1F]" size={20} strokeWidth={2.5} />
              </div>
              <div>
                <div className="stamp mb-1">Phone</div>
                <div className="font-display text-2xl font-bold text-white group-hover:text-[#FF5A1F] transition">(201) 822-1260</div>
              </div>
            </a>
            <a href="mailto:info@synnify.com" className="flex items-start gap-5 group" data-testid="contact-email">
              <div className="w-12 h-12 border border-[#262626] flex items-center justify-center group-hover:border-[#FF5A1F] group-hover:bg-[#FF5A1F]/10 transition">
                <Mail className="text-[#FF5A1F]" size={20} strokeWidth={2.5} />
              </div>
              <div>
                <div className="stamp mb-1">Email</div>
                <div className="font-display text-2xl font-bold text-white group-hover:text-[#FF5A1F] transition">info@synnify.com</div>
              </div>
            </a>
            <div className="flex items-start gap-5" data-testid="contact-address">
              <div className="w-12 h-12 border border-[#262626] flex items-center justify-center">
                <MapPin className="text-[#FF5A1F]" size={20} strokeWidth={2.5} />
              </div>
              <div>
                <div className="stamp mb-1">HQ</div>
                <div className="font-display text-xl font-bold text-white leading-tight">
                  3626 Wyoming St
                  <br />
                  Houston, TX 77021
                </div>
              </div>
            </div>
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 border border-[#262626] flex items-center justify-center">
                <Clock className="text-[#FF5A1F]" size={20} strokeWidth={2.5} />
              </div>
              <div>
                <div className="stamp mb-1">Dispatch Hours</div>
                <div className="font-display text-xl font-bold text-white">24 / 7 / 365</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 bg-[#141414] border border-[#262626] p-6 md:p-10 relative corner-brackets" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="stamp block mb-2">Name *</label>
              <input className="industrial-input" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="contact-name" />
            </div>
            <div>
              <label className="stamp block mb-2">Email *</label>
              <input type="email" className="industrial-input" placeholder="you@email.com" value={form.email} onChange={set("email")} data-testid="contact-email-input" />
            </div>
            <div>
              <label className="stamp block mb-2">Phone</label>
              <input className="industrial-input" placeholder="(555) 555-5555" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
            </div>
            <div>
              <label className="stamp block mb-2">Subject</label>
              <input className="industrial-input" placeholder="Dispatch inquiry" value={form.subject} onChange={set("subject")} data-testid="contact-subject" />
            </div>
            <div className="md:col-span-2">
              <label className="stamp block mb-2">Message *</label>
              <textarea className="industrial-input" placeholder="Tell us about your operation..." value={form.message} onChange={set("message")} data-testid="contact-message" />
            </div>
          </div>
          <div className="mt-8 flex items-center justify-between">
            <span className="text-xs text-[#737373] font-mono-plex uppercase tracking-widest">* Required</span>
            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="contact-submit">
              {loading ? "Sending..." : "Send Message"} <ArrowRight size={18} strokeWidth={3} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

/* ---------- CTA BAND ---------- */
const CtaBand = () => (
  <section className="relative py-20 overflow-hidden" data-testid="cta-band">
    <div className="diagonal-divider absolute top-0 left-0 right-0" />
    <div className="diagonal-divider absolute bottom-0 left-0 right-0" />
    <div className="container mx-auto px-6 md:px-8 max-w-7xl py-10 text-center">
      <h3 className="font-display text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight text-white">
        Tired of hunting loads at 2 a.m.?
      </h3>
      <p className="mt-4 text-[#A3A3A3] text-lg max-w-2xl mx-auto">
        Let Synnify's dispatch desk take the wheel on your back-office.
      </p>
      <div className="mt-8 flex justify-center gap-4 flex-wrap">
        <a href="#onboarding" className="btn-primary" data-testid="cta-band-onboard">
          Start Hauling <ArrowRight size={18} strokeWidth={3} />
        </a>
        <a href="tel:+12018221260" className="btn-secondary" data-testid="cta-band-call">
          Call (201) 822-1260
        </a>
      </div>
    </div>
  </section>
);

/* ---------- FOOTER ---------- */
const Footer = () => (
  <footer className="relative bg-[#0A0A0A] pt-20 pb-8 border-t border-[#262626]" data-testid="footer">
    <div className="container mx-auto px-6 md:px-8 max-w-7xl">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Synnify" className="h-12 w-12 object-contain" />
            <span className="font-display text-2xl font-black uppercase text-white">Synnify</span>
          </div>
          <p className="mt-5 text-[#A3A3A3] leading-relaxed max-w-md">
            A logistics support company built for owner-operators and small fleets. We dispatch.
            You drive. Everyone wins.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="stamp mb-4">Company</div>
          <ul className="space-y-3">
            <li><a href="#about" className="text-[#A3A3A3] hover:text-[#FF5A1F] transition">About</a></li>
            <li><a href="#services" className="text-[#A3A3A3] hover:text-[#FF5A1F] transition">Services</a></li>
            <li><a href="#pricing" className="text-[#A3A3A3] hover:text-[#FF5A1F] transition">Pricing</a></li>
            <li><a href="#why-us" className="text-[#A3A3A3] hover:text-[#FF5A1F] transition">Why Synnify</a></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="stamp mb-4">Contact</div>
          <ul className="space-y-3">
            <li className="text-[#A3A3A3]"><span className="text-white font-semibold">Phone: </span><a href="tel:+12018221260" className="hover:text-[#FF5A1F] transition">(201) 822-1260</a></li>
            <li className="text-[#A3A3A3]"><span className="text-white font-semibold">Email: </span><a href="mailto:info@synnify.com" className="hover:text-[#FF5A1F] transition">info@synnify.com</a></li>
            <li className="text-[#A3A3A3]"><span className="text-white font-semibold">HQ: </span>3626 Wyoming St, Houston TX 77021</li>
          </ul>
        </div>
      </div>

      {/* Giant wordmark */}
      {/* <div className="border-t border-[#262626] pt-12 overflow-hidden">
        <div className="font-display font-black uppercase tracking-tighter text-transparent leading-[0.85] text-center select-none" style={{ WebkitTextStroke: "1px #262626", fontSize: "clamp(4rem, 18vw, 18rem)" }}>
          Synnify
        </div>
      </div> */}

      <div className="mt-10 pt-8 border-t border-[#262626] flex flex-col md:flex-row items-center justify-between gap-4 text-[#737373] text-xs font-mono-plex uppercase tracking-widest">
        <span>© {new Date().getFullYear()} Synnify LLC. All rights reserved.</span>
        <span>Not a broker. Not a carrier. Dispatch support only.</span>
      </div>
    </div>
  </footer>
);

/* ---------- APP ---------- */
function App() {
  useEffect(() => {
    // Ensure dark theme class is applied
    document.documentElement.classList.add("dark");
  }, []);

  return (
    <div className="App min-h-screen bg-[#0A0A0A] text-white">
      <Toaster position="top-right" theme="dark" toastOptions={{
        style: { background: "#141414", color: "#fff", border: "1px solid #262626", borderRadius: 0 }
      }} />
      <Navbar />
      <main>
        <Hero />
        <Marquee />
        <Stats />
        <Disclaimer />
        <Services />
        <WhyUs />
        <Pricing />
        <Onboarding />
        <About />
        <FAQ />
        <Contact />
        <CtaBand />
      </main>
      <Footer />
    </div>
  );
}

export default App;
